import { Suspense } from 'react';

import { ErrorBoundary } from '../../components/error-boundary';
import { ReactionBar } from '../reactions/reaction-bar';
import { CommentList } from '../comments/comment-list';
import { ShareActions } from '../../components/share-actions';
import { SurveyBody } from './prose-body';
import { ArticleOwnerActions } from './article-owner-actions';
import { ArticlePaper } from './article-paper';
import { formatDate } from './format';
import { useArticle } from './use-article';
import { useMe } from '../auth/use-me';
import { dropLeadingHeading } from '@zoxo/shared';

/**
 * 좋소 PD 수첩 article detail (V4 §C). Readability-first, Mobile First.
 *
 * Renders the tiptap body (reusing the survey read renderer), author (nickname +
 * company initials only), date and view count. An unapproved (isActive=false)
 * article is only reachable by its author/admin — they see a "검수 대기" badge.
 * Comments reuse the generalized 2-level thread (article target). Comments get
 * their own Suspense/ErrorBoundary so a comments failure doesn't blank the article.
 */
export function ArticleDetail({ id }: { id: string }) {
  const { data: article } = useArticle(id);
  const { data: me } = useMe();

  // 작성자 또는 관리자. 옛 응답(canEdit 없음)은 isMine 으로 돌아간다.
  const canEdit = article.canEdit ?? article.isMine ?? false;
  const isAdmin = me?.role === 'admin';
  return (
    <article>
      {/*
        어디에 있는지는 머리의 최상위 메뉴(아티클 알약)가 말한다.
        수정·삭제는 태그 줄 아래 한 곳(§ArticleOwnerActions). 상태 줄은 상자 없이 —
        머리·제목과 왼쪽 끝이 맞아야 한 열로 읽힌다.
      */}
      {article.status === 'draft' && (
        <div
          className="mb-6 flex items-start gap-2 text-sm leading-relaxed text-text-muted"
          role="status"
        >
          <p className="flex-1">
            <strong className="font-medium text-text">초안</strong> 아직 제출하지
            않은 초안이에요. 수정 화면에서 “제출”하면 관리자 검수 후 전지적 좋소 시점에
            노출돼요. 지금은 작성자만 볼 수 있어요.
          </p>
        </div>
      )}
      {article.status === 'pending' && (
        <div
          className="mb-6 flex items-start gap-2 text-sm leading-relaxed text-text-muted"
          role="status"
        >
          <p className="flex-1">
            <strong className="font-bold text-[#DF301C]">검수대기</strong> 이 기사는 아직
            관리자 승인 이전 미공개 상태입니다. 지금은 작성자만 볼 수 있어요.
          </p>
        </div>
      )}
      {/* /grants 의 「자금·바우처」과 같은 크기 — 사이트 최대 제목. */}
      <h1 className="mb-3 text-2xl font-bold leading-tight tracking-tight text-gray-900">
        {article.title}
      </h1>

      <div className="mb-6 flex flex-wrap items-center gap-1.5 border-b border-border pb-3 text-sm text-text-muted">
        {/* 닉네임만 — 실명·식별자 없음. */}
        <span>{article.author.nickname}</span>
        <span aria-hidden="true">·</span>
        <span>{formatDate(article.createdAt)}</span>
        <span aria-hidden="true">·</span>
        <span>조회 {article.viewCount}</span>
      </div>

      {/* 태그(왼쪽)와 「✎ 수정」(오른쪽 끝)이 한 줄. 둘 다 없으면 줄 자체가 없다. */}
      {(article.tags && article.tags.length > 0) || canEdit ? (
        <div className="mb-6 flex items-center justify-between gap-3">
          <ul className="flex flex-wrap items-center gap-1.5">
            {(article.tags ?? []).map((t) => (
              <li
                key={t}
                className="rounded-md bg-brand-soft px-2 py-0.5 text-xs font-medium text-brand-strong"
              >
                #{t}
              </li>
            ))}
          </ul>
          {/* 작성자: 수정. 관리자: 수정 + 삭제. */}
          {canEdit ? <ArticleOwnerActions articleId={article.id} canDelete={isAdmin} /> : null}
        </div>
      ) : null}

      {/* 본문 맨 앞 h1 은 페이지 제목과 겹쳐 보여 뗀다 — SSR 과 같은 규칙. 종이는 커뮤니티와 같은 부품. */}
      <ArticlePaper>
        <SurveyBody body={dropLeadingHeading(article.body)} />
      </ArticlePaper>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-3 border-t border-border pt-3">
        {/* Reactions only on public (approved) articles — a pending preview has none. */}
        {article.isActive ? (
          <ReactionBar
            target={{ kind: 'article', id: article.id }}
            likeCount={article.likeCount}
            dislikeCount={article.dislikeCount}
            myReaction={article.myReaction}
          />
        ) : (
          <span />
        )}
        <ShareActions
          targetId={article.id}
          path={`/articles/${article.id}`}
          title={article.title}
          description={article.summary || article.title}
        />
      </div>

      {/* 댓글: 발행된 글은 누구나, 검수 대기·초안은 작성자(와 운영자)에게만 —
          API 가 같은 규칙으로 남에게는 404 를 준다. */}
      {(article.isActive || canEdit) && (
        <ErrorBoundary
          resetKey={id}
          fallback={() => (
            <p className="text-sm text-text-muted">댓글을 불러오지 못했어요.</p>
          )}
        >
          <Suspense
            fallback={
              <p className="text-sm text-text-muted">댓글 불러오는 중…</p>
            }
          >
            <CommentList target={{ kind: 'article', id }} />
          </Suspense>
        </ErrorBoundary>
      )}
    </article>
  );
}
